import { Subject } from "rxjs";
import {
  extractNumber,
  generateUUID,
  maxDotNumber,
  toKebab,
  toObjectString,
  withinErrorMarginNumber
} from "@zyzgroup/core-common";
import { Arc, L, PathD } from "./line";
import {
  SVGParse,
  SVGParsedPathData,
  absolutizeSVGPath,
  isSVGPath,
  parseSVGPath,
  relativeSVGPath
} from "./svg";
import { YZCanvasContext } from "./canvas";
import {
  distance,
  angle,
  polar,
  toDegree,
  toRadian,
  parseTransformToMatrix,
  Matrix,
  rotatePoint
} from "../../math";
import Timeline, { TweenRawOptions, TweenValue } from "../../animate/Timeline";
import { Color } from "../../color";
import { MousePointGroup } from "../useElementEvents";

export type ShapeBaseOptions = {
  // 强制设置 SVG path d, 优先级比 toSVGPathData() 高
  // 传入 initD ，即代表为 静态Shape
  initD?: string | SVGParsedPathData[] | PathD;
};

export type ShapeAttributeOptions = {
  // 属性
  attrs?: Record<string, string | CanvasGradient>;
  // 样式
  style?: Record<string, string>;
};

export type ShapeTransformOptions = {
  // transform origin: 相对于自身（0,0）的坐标
  centerX?: number;
  centerY?: number;

  // translate: 相对于自身（0,0）的坐标
  x?: number;
  y?: number;

  // rotate: 相对于自身
  degree?: number;

  // scale: 相对于自身
  scale?: number;
  scaleX?: number;
  scaleY?: number;
};

export type ShapeAnimateOptions = {
  // 速度
  vx?: number;
  vy?: number;

  // 加速度
  ax?: number;
  ay?: number;

  // 重量
  mass?: number;
};
export type ShapeAnimateConstantOptions = {
  // 重力常量，例如 0.1 0.5
  gravity?: number;
  // 是否开启万有引力
  enableGravityBetweenObjects?: boolean;

  // 摩擦系数常量，例如 1
  friction?: number;

  // 例如 0.05
  easing?: number;

  // 例如 0.03 0.0005
  spring?: number;
  // 例如 100
  springLength?: number;

  // 回弹检测
  wall?: {
    x: number;
    y: number;
    width: number;
    height: number;
    cycle?: boolean;
    // 回弹常量，例如 -1
    bounce?: number;
  };

  // 是否开启碰撞检测
  enableCollision?: boolean;

  enableSpringMassAlpha?: boolean;
};

export type ShapeDrawOptions = {
  // 是否可交互: 性能影响，可关闭检测 isMouseIn
  interactive?: boolean;

  // 是否可拖拽
  drag?: {
    enable?: boolean;
    // 如果 parent.combo.shapes 其中一个 drag 了，自己也跟着 move
    followSibling?: boolean;
    // 拖拽时沿的路线，相对值
    alongPath?: PathD;
  };
};

export type ShapeOptions = ShapeBaseOptions &
  ShapeAttributeOptions &
  ShapeTransformOptions &
  ShapeAnimateOptions &
  ShapeDrawOptions;

export class Shape {
  [index: string | number | symbol]: any;

  // this.shouldUpdatePathD 交给子类变更
  public shouldUpdatePathD = false;
  public shouldUpdateTransformPathD = false;
  public shouldUpdateSVGElInDraw = false;
  public shouldUpdateMInDraw = true;
  public shouldUpdateAttrsInDraw = true;
  public shouldUpdateStyleInDraw = true;

  public id: string;
  public initD?: string | SVGParsedPathData[] | PathD;
  public d?: PathD;
  public parent?: {
    combo: ShapeCombo;
    dX: number;
    dY: number;
    dDegree: number;
    dScaleX: number;
    dScaleY: number;
  };

  public attrs: Record<string, string | CanvasGradient> = {};
  public style: Record<string, string> = {};

  private initCenterX?: number;
  private initCenterY?: number;

  private _x: number;
  private _y: number;

  private _degree: number;

  private _scaleX: number;
  private _scaleY: number;

  public vx: number;
  public vy: number;
  public ax: number;
  public ay: number;
  public mass: number;

  public interactive: boolean;
  public drag?: ShapeDrawOptions["drag"];

  public transform$ = new Subject<{
    dX?: number;
    dY?: number;
    dDegree?: number;
    dScaleX?: number;
    dScaleY?: number;
  }>();
  private transformSubscription: any;

  // DrawContext 动态注入参数
  public isMouseIn = false;
  public isDragging = false;
  public m?: Matrix | DOMMatrix;
  public el?: HTMLElement | SVGElement;

  constructor(options?: ShapeOptions) {
    this.id = generateUUID(4);
    this.initD = options?.initD;

    this.setAttr({
      // "paint-order": "markers stroke fill", // 先 stroke 再 fill
      // "paint-order": "fill stroke markers", // default
      stroke: "none",
      fill: "none"
      // "pointerEvents": "none",
    });
    if (options?.attrs) {
      this.setAttr(options.attrs);
    }
    if (options?.style) {
      this.setStyle(options.style);
    }

    this.initCenterX = options?.centerX;
    this.initCenterY = options?.centerY;

    this._x = options?.x || 0;
    this._y = options?.y || 0;

    this._degree = options?.degree || 0;

    this._scaleX = 1;
    this._scaleY = 1;
    if (options?.scale !== void 0) {
      this._scaleX = options.scale;
      this._scaleY = options.scale;
    }
    if (options?.scaleX !== void 0) {
      this._scaleX = options.scaleX;
    }
    if (options?.scaleY !== void 0) {
      this._scaleY = options.scaleY;
    }

    this.vx = options?.vx || 0;
    this.vy = options?.vy || 0;
    this.ax = options?.ax || 0;
    this.ay = options?.ay || 0;
    this.mass = options?.mass || 1;

    this.interactive =
      typeof options?.interactive == "boolean" ? options?.interactive : true;
    this.drag = options?.drag;

    // return new Proxy(this, {
    //   set(target, prop, value) {
    //     // console.log(`Property ${String(prop)} is being set to ${value}`);
    //     if (
    //       prop == "_x" ||
    //       prop == "_y" ||
    //       prop == "_degree" ||
    //       prop == "_scaleX" ||
    //       prop == "_scaleY"
    //     ) {
    //       // console.log(`transform 不变更 PathD`);
    //     } else {
    //       // console.log(`Should Change PathD!`);
    //       (this as Shape).shouldUpdatePathD = true;
    //     }
    //     target[prop] = value;
    //     return true;
    //   }
    // });

    this.transformSubscription = this.transform$.subscribe(() => {
      // 提升性能：延迟到使用时再计算
      this.shouldUpdateTransformPathD = true;
      this.shouldUpdateMInDraw = true;
    });
  }

  public destroy() {
    this.transformSubscription.unsubscribe();
  }

  // 基操
  public moveTo(x: number, y: number) {
    const prevX = this._x;
    const prevY = this._y;
    if (prevX !== x || prevY !== y) {
      this._x = x;
      this._y = y;
      this.transform$.next({ dX: x - prevX, dY: y - prevY });
    }
  }
  public move(dx: number, dy = 0) {
    this.moveTo(this._x + dx, this._y + dy);
  }
  public moveXTo(x: number) {
    this.moveTo(x, this._y);
  }
  public moveX(dx: number) {
    this.moveTo(this._x + dx, this._y);
  }
  public moveYTo(y: number) {
    this.moveTo(this._x, y);
  }
  public moveY(dy: number) {
    this.moveTo(this._x, this._y + dy);
  }
  public get x(): number {
    return this._x;
  }
  // 基操
  public set x(v: number) {
    if (this._x !== v) {
      this.transform$.next({ dX: v - this._x });
      this._x = v;
    }
  }
  public get y(): number {
    return this._y;
  }
  // 基操
  public set y(v: number) {
    if (this._y !== v) {
      this.transform$.next({ dY: v - this._y });
      this._y = v;
    }
  }
  // 基操
  public rotateTo(degree: number) {
    if (this._degree !== degree) {
      this.transform$.next({ dDegree: degree - this._degree });
      this._degree = degree;
    }
  }
  public rotate(degree: number) {
    this.rotateTo(this._degree + degree);
  }
  public get degree(): number {
    return this._degree;
  }
  // 基操
  public set degree(v: number) {
    if (this._degree !== v) {
      this.transform$.next({ dDegree: v - this._degree });
      this._degree = v;
    }
  }
  // 基操
  public scaleTo(scaleX: number, scaleY?: number) {
    const prevScaleX = this._scaleX;
    const prevScaleY = this._scaleY;
    if (this._scaleX !== scaleX || this._scaleY !== (scaleY || scaleX)) {
      this._scaleX = scaleX;
      this._scaleY = scaleY || scaleX;
      this.transform$.next({
        dScaleX: this._scaleX / prevScaleX,
        dScaleY: this._scaleY / prevScaleY
      });
    }
  }
  public scale(scaleX: number, scaleY?: number) {
    this.scaleTo(this._scaleX * scaleX, this._scaleY * (scaleY || scaleX));
  }
  public get scaleX(): number {
    return this._scaleX;
  }
  // 基操
  public set scaleX(v: number) {
    if (this._scaleX !== v) {
      this.transform$.next({
        dScaleX: v / this._scaleX
      });
      this._scaleX = v;
    }
  }
  public get scaleY(): number {
    return this._scaleY;
  }
  // 基操
  public set scaleY(v: number) {
    if (this._scaleY !== v) {
      this.transform$.next({
        dScaleY: v / this._scaleY
      });
      this._scaleY = v;
    }
  }

  // 无法在 constructor 中获得 子类.toSVGPathData()
  public pathD(force?: PathD): PathD {
    if (force) {
      this.d = force;
      this.shouldUpdateTransformPathD = true;
      this.shouldUpdateSVGElInDraw = true;
    } else if (this.initD) {
      // 静态-Shape 可以缓存
      if (!this.d) {
        this.d =
          this.initD instanceof PathD ? this.initD : new PathD(this.initD);
      }
    } else {
      // 解决性能瓶颈：加入缓存, 减少计算, transform 不变更 PathD
      // this.shouldUpdatePathD 交给子类变更
      if (!this.d || this.shouldUpdatePathD) {
        // 子类.toSVGPathData() 可能会动态变化， 所以间歇缓存
        this.d = new PathD(this.toSVGPathData());
        this.shouldUpdatePathD = false;
        this.shouldUpdateTransformPathD = true;
        this.shouldUpdateSVGElInDraw = true;
      }
    }
    return this.d;
  }

  protected cachedTransformedPathD?: PathD;
  public transformPathD(): PathD {
    if (!this.m) {
      // 初次计算，与 DrawContext 内一致
      // transform origin - 不缓存, 直接使用 getBoundingClientRectCenter， 间接使用 getBoundingClientRect
      const { x: centerX, y: centerY } = this.transformOrigin();
      let centerXSelf = 0;
      let centerYSelf = 0;
      if (
        this.parent?.dDegree ||
        this.parent?.dScaleX !== 1 ||
        this.parent?.dScaleY !== 1
      ) {
        const tos = this.transformOrigin(true);
        centerXSelf = tos.x;
        centerYSelf = tos.y;
      }
      // 移动的是坐标系 the order of these are important !
      const m = new Matrix();
      // 公共
      m.translate(
        this.x + (this.parent?.dX || 0),
        this.y + (this.parent?.dY || 0)
      );
      m.rotate(this.degree, centerX, centerY);
      m.scale(this.scaleX, this.scaleY, centerX, centerY);
      // 自己
      if (this.parent?.dDegree) {
        m.rotate(this.parent.dDegree, centerXSelf, centerYSelf);
      }
      if (this.parent?.dScaleX !== 1 || this.parent?.dScaleY !== 1) {
        m.scale(
          this.parent?.dScaleX || 1,
          this.parent?.dScaleY || 1,
          centerXSelf,
          centerYSelf
        );
      }
      // 缓存计算结果
      this.m = m;
    }
    if (this.shouldUpdateTransformPathD || !this.cachedTransformedPathD) {
      this.cachedTransformedPathD = this.pathD().transform({ m: this.m });
      this.shouldUpdateTransformPathD = false;
    }
    return this.cachedTransformedPathD;
  }

  public getBaseOptions() {
    return {
      initD: this.initD,

      attrs: { ...this.attrs },
      style: { ...this.style },

      centerX: this.initCenterX,
      centerY: this.initCenterY,

      x: this._x,
      y: this._y,

      degree: this._degree,

      scaleX: this._scaleX,
      scaleY: this._scaleY,

      vx: this.vx,
      vy: this.vy,
      ax: this.ax,
      ay: this.ay,
      mass: this.mass,

      interactive: this.interactive,
      drag: { ...this.drag }
    };
  }

  public clone() {
    const shape = this.cloneShape();
    // if (this.parent) {
    //   shape.parent = { ...this.parent };
    // }
    return shape;
  }

  // 子类需覆盖！
  protected cloneShape() {
    return new Shape(this.getBaseOptions());
  }

  // 供外部使用：基本属性变更
  public update(options?: Partial<ShapeOptions>) {
    this.shouldUpdatePathD = true;
  }

  // 优先级比 toSVGPathData() 高
  // 使用相对值! transform 交给 DrawContext
  public toSVGEl(): SVGElement | null {
    // SVGElement = 子类覆盖!
    return null;
  }

  // shape 内部定义
  // 使用相对值! transform 交给 DrawContext
  public toSVGPathData(): SVGParsedPathData[] {
    // 子类需覆盖!
    return [];
  }

  // 外部使用
  // 使用相对值!
  public toSVGPathD(): string {
    return this.pathD().toSVGPathD();
  }

  // 使用相对值! transform 交给 DrawContext
  // ctx.createLinearGradient 使用相对值
  // 如果子类没有覆盖 toCanvasContext , 默认使用 PathD 的 toCanvasContext
  // 如果子类覆盖 toCanvasContext , 则使用 原生canvas系统
  public toCanvasContext(ctx: YZCanvasContext): void {
    this.pathD().toCanvasContext(ctx);
  }

  // 子类可覆盖
  // 相对坐标系原点 - 坐标系没有考虑 transform : translate rotate scale
  public getBoundingClientRect(dot = 2) {
    return this.pathD().getBoundingClientRect(dot);
  }

  // 相对坐标系原点 - 坐标系加入考虑 transform : translate rotate scale
  public getRealBoundingClientRect(dot = 2) {
    return this.transformPathD().getBoundingClientRect(dot);
  }

  // 相对坐标系原点 - 坐标系没有考虑 transform : translate rotate scale
  public getBoundingClientRectCenter(dot = 2) {
    return this.pathD().getBoundingClientRectCenter(dot);
  }

  // 相对坐标系原点 - 坐标系加入考虑 transform : translate rotate scale
  public getRealBoundingClientRectCenter(dot = 2) {
    return this.transformPathD().getBoundingClientRectCenter(dot);
  }

  // transform origin - 不缓存, 直接使用 getBoundingClientRectCenter， 间接使用 getBoundingClientRect
  // 相对坐标系原点 - 坐标系没有考虑 transform : translate rotate scale
  public transformOrigin(forceSelf = false, dot = 2): { x: number; y: number } {
    let x = 0;
    let y = 0;
    if (this.parent && !forceSelf) {
      const parentRectCenter =
        this.parent.combo.getBoundingClientRectCenter(dot);
      x = parentRectCenter.x - this.parent.dX;
      y = parentRectCenter.y - this.parent.dY;
    } else {
      if (
        typeof this.initCenterX == "undefined" &&
        typeof this.initCenterY == "undefined"
      ) {
        // 没有强制设置 transform origin 时，默认为 中心点
        const selfRectCenter = this.getBoundingClientRectCenter(dot);
        x = selfRectCenter.x;
        y = selfRectCenter.y;
      } else {
        // 强制设置 transform origin
        x = this.initCenterX || 0;
        y = this.initCenterY || 0;
      }
    }
    return { x, y };
  }

  public isPointInPath(
    ctx: YZCanvasContext | SVGElement,
    x: number,
    y: number
  ): boolean {
    // Canvas
    if (ctx instanceof YZCanvasContext) {
      return ctx.isPointInPath(new Path2D(this.toSVGPathD()), x, y);
    }
    // SVG
    if (this.el instanceof SVGGeometryElement) {
      // 以下也是可用的，但性能未知
      const matrix = this.el.getCTM();
      if (matrix) {
        const invertedMatrix = matrix.inverse();
        return this.el.isPointInFill({
          x: invertedMatrix.a * x + invertedMatrix.c * y + invertedMatrix.e,
          y: invertedMatrix.b * x + invertedMatrix.d * y + invertedMatrix.f
        });
      }
      // 以下也是可用的，但性能未知
      // if (this.m) {
      //   const invertedMatrix =
      //     this.m instanceof Matrix
      //       ? this.m.clone().inverse()
      //       : this.m.inverse();
      //   if (invertedMatrix) {
      //     return this.el.isPointInFill(invertedMatrix.transformPoint({ x, y }));
      //   }
      // }
    }
    return false;
  }

  public isPointInStroke(
    ctx: YZCanvasContext | SVGElement,
    x: number,
    y: number
  ): boolean {
    // Canvas
    if (ctx instanceof YZCanvasContext) {
      return ctx.isPointInStroke(new Path2D(this.toSVGPathD()), x, y);
    }
    // SVG
    if (this.el instanceof SVGGeometryElement) {
      // 以下也是可用的，但性能未知
      const matrix = this.el.getCTM();
      if (matrix) {
        const invertedMatrix = matrix.inverse();
        return this.el.isPointInStroke({
          x: invertedMatrix.a * x + invertedMatrix.c * y + invertedMatrix.e,
          y: invertedMatrix.b * x + invertedMatrix.d * y + invertedMatrix.f
        });
      }
      // 以下也是可用的，但性能未知
      // if (this.m) {
      //   const invertedMatrix =
      //     this.m instanceof Matrix
      //       ? this.m.clone().inverse()
      //       : this.m.inverse();
      //   if (invertedMatrix) {
      //     return this.el.isPointInStroke(
      //       invertedMatrix.transformPoint({ x, y })
      //     );
      //   }
      // }
    }
    // UNDO: node-canvas return undefined
    return false;
  }

  public setStyle(obj: Record<string, string>): void {
    Object.assign(this.style, obj);
    this.shouldUpdateStyleInDraw = true;
  }

  public setAttr(obj: Record<string, string | CanvasGradient>): void;
  public setAttr(name: string, value: string | CanvasGradient): void;
  public setAttr(
    nameOrObj: string | Record<string, string | CanvasGradient>,
    value?: string | CanvasGradient
  ): void {
    if (typeof nameOrObj !== "string") {
      for (const k in nameOrObj) {
        const v = nameOrObj[k];
        if (v && v != this.getAttr(k)) {
          this.attrs[toKebab(k)] = v;
          this.shouldUpdateAttrsInDraw = true;
        }
      }
    } else {
      if (value && value != this.getAttr(nameOrObj)) {
        this.attrs[toKebab(nameOrObj)] = value;
        this.shouldUpdateAttrsInDraw = true;
      }
    }
  }

  public getAttr(
    name: string,
    defaultValue: string | CanvasGradient
  ): string | CanvasGradient;
  public getAttr(name: string): string | CanvasGradient | undefined;
  public getAttr(
    name: string,
    defaultValue?: string | CanvasGradient
  ): string | CanvasGradient | undefined {
    return this.attrs[name] || this.attrs[toKebab(name)] || defaultValue;
  }

  public hasAttr(name: string): boolean {
    return !!this.getAttr(name);
  }

  public animate(
    options?: Pick<ShapeAnimateConstantOptions, "gravity" | "friction">
  ) {
    // 加速度
    this.vx += this.ax;
    this.vy += this.ay;

    // 重力
    const gravity = options?.gravity || 0;
    this.vy += gravity;

    // 摩擦力方式一
    // const friction = options?.friction || 0;
    // if (friction > 0) {
    //   const radian = Math.atan2(this.vy, this.vx);
    //   let speed = Math.sqrt(this.vx * this.vx + this.vy * this.vy);
    //   if (speed > friction) {
    //     speed -= friction;
    //   } else {
    //     speed = 0;
    //   }
    //   this.vx = Math.cos(radian) * speed;
    //   this.vy = Math.sin(radian) * speed;
    // }

    // 摩擦力方式二
    const friction = options?.friction || 1;
    this.vx *= friction;
    this.vy *= friction;

    this.move(this.vx, this.vy);
  }

  public animateEaseTo(
    target: { x: number; y: number },
    options?: Pick<
      ShapeAnimateConstantOptions,
      "easing" | "gravity" | "friction"
    >
  ) {
    const easing = options?.easing || 0.05;
    const dx = target.x - this.x;
    const dy = target.y - this.y;
    this.vx = dx * easing;
    this.vy = dy * easing;
    this.animate(options);
  }

  public animateSpringTo(
    target: { x: number; y: number } | { x: number; y: number }[],
    options?: Pick<
      ShapeAnimateConstantOptions,
      "spring" | "gravity" | "friction"
    >
  ) {
    const spring = options?.spring || 0.03;
    (Array.isArray(target) ? target : [target]).forEach((t) => {
      const dx = t.x - this.x;
      const dy = t.y - this.y;
      const ax = dx * spring;
      const ay = dy * spring;
      this.vx += ax;
      this.vy += ay;
    });
    this.animate({ friction: 0.9, ...options });
  }

  public getSpringMassAlphaTo(
    target: Shape,
    options?: Pick<
      ShapeAnimateConstantOptions,
      "spring" | "springLength" | "gravity" | "friction"
    >
  ): number {
    const springLength = options?.springLength || 100;
    const spring = options?.spring || 0.0005;
    const dx = target.x - this.x;
    const dy = target.y - this.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < springLength) {
      const ax = dx * spring;
      const ay = dy * spring;
      this.vx += ax / this.mass;
      this.vy += ay / this.mass;
      target.vx -= ax / target.mass;
      target.vy -= ay / target.mass;
      return 1 - dist / springLength; // alpha: 0 ~ 1
    }
    return 0;
  }

  public followTo(target: { x: number; y: number }, length = 3) {
    this.rotateTo(toDegree(angle(this.x, this.y, target.x, target.y)));
    const { dx: vx, dy: vy } = polar(toRadian(this.degree), length);
    this.vx = vx;
    this.vy = vy;
    this.animate();
  }

  public followAccelerationTo(target: { x: number; y: number }, length = 0.05) {
    this.rotateTo(toDegree(angle(this.x, this.y, target.x, target.y)));
    const { dx: ax, dy: ay } = polar(toRadian(this.degree), length);
    this.ax = ax;
    this.ay = ay;
    this.animate();
  }

  // 物体之间引力
  public gravitateTo(target: Shape) {
    const dx = target.x - this.x;
    const dy = target.y - this.y;
    const distSQ = dx * dx + dy * dy;
    const dist = Math.sqrt(distSQ);
    const force = (this.mass * target.mass) / distSQ;
    const ax = (force * dx) / dist;
    const ay = (force * dy) / dist;
    this.vx += ax / this.mass;
    this.vy += ay / this.mass;
    target.vx -= ax / target.mass;
    target.vy -= ay / target.mass;
  }

  // 碰撞检测
  public checkCollision(target: Shape) {
    // 子类按需重写
    const dx = target.x - this.x;
    const dy = target.y - this.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    const {
      x: targetX,
      y: targetY,
      width: targetWidth,
      height: targetHeight
    } = target.getBoundingClientRect();
    const { x: targetCenterX, y: targetCenterY } =
      target.getBoundingClientRectCenter();
    const { x, y, width, height } = this.getBoundingClientRect();
    const { x: centerX, y: centerY } = this.getBoundingClientRectCenter();
    // if (dist < this.ballRadius + target.ballRadius) {
    if (
      dist < Math.abs(targetCenterX - targetX) + Math.abs(centerX - x) ||
      dist < Math.abs(targetCenterY - targetY) + Math.abs(centerY - y)
    ) {
      const radian = Math.atan2(dy, dx);
      // rotate ball0's position
      const pos0 = { x: 0, y: 0 };
      // rotate ball1's position
      const pos1 = rotatePoint(-radian, dx, dy);
      // rotate ball0's velocity
      const vel0 = rotatePoint(-radian, this.vx, this.vy);
      // rotate ball1's velocity
      const vel1 = rotatePoint(-radian, target.vx, target.vy);
      // collision reaction
      const vxTotal = vel0.x - vel1.x;
      vel0.x =
        ((this.mass - target.mass) * vel0.x + 2 * target.mass * vel1.x) /
        (this.mass + target.mass);
      vel1.x = vxTotal + vel0.x;
      // update position - to avoid objects becoming stuck together
      const absV = Math.abs(vel0.x) + Math.abs(vel1.x);
      const overlap =
        this.ballRadius + target.ballRadius - Math.abs(pos0.x - pos1.x);
      pos0.x += (vel0.x / absV) * overlap;
      pos1.x += (vel1.x / absV) * overlap;
      // rotate positions back
      const pos0F = rotatePoint(radian, pos0.x, pos0.y);
      const pos1F = rotatePoint(radian, pos1.x, pos1.y);
      // adjust positions to actual screen positions
      target.moveTo(this.x + pos1F.x, this.y + pos1F.y);
      this.moveTo(this.x + pos0F.x, this.y + pos0F.y);
      // rotate velocities back
      const vel0F = rotatePoint(radian, vel0.x, vel0.y);
      const vel1F = rotatePoint(radian, vel1.x, vel1.y);
      this.vx = vel0F.x;
      this.vy = vel0F.y;
      target.vx = vel1F.x;
      target.vy = vel1F.y;
    }
  }

  // 回弹检测 或 重回检测
  public checkWall(options?: ShapeAnimateConstantOptions) {
    // 子类按需重写
    if (!options?.wall) {
      return;
    }
    const bounce =
      typeof options.wall.bounce == "number" ? options.wall.bounce : -1;
    const left = options.wall.x;
    const top = options.wall.y;
    const right = options.wall.x + options.wall.width;
    const bottom = options.wall.y + options.wall.height;
    const { x, y, width, height } = this.getBoundingClientRect();
    // 碰撞后是 回弹 还是 重回
    const isCycle = options.wall.cycle || false;
    if (!isCycle) {
      // 回弹检测
      if (this.x + x + width > right) {
        this.moveXTo(right - (x + width));
        this.vx *= bounce;
      } else if (this.x + x < left) {
        this.moveXTo(left + (x + width));
        this.vx *= bounce;
      }
      if (this.y + y + height > bottom) {
        this.moveYTo(bottom - (y + height));
        this.vy *= bounce;
      } else if (this.y + y < top) {
        this.moveYTo(top + (y + height));
        this.vy *= bounce;
      }
      // if (this.x + this.ballRadius > right) {
      //   this.moveXTo(right - this.ballRadius);
      //   this.vx *= bounce;
      // } else if (this.x - this.ballRadius < left) {
      //   this.moveXTo(left + this.ballRadius);
      //   this.vx *= bounce;
      // }
      // if (this.y + this.ballRadius > bottom) {
      //   this.moveYTo(bottom - this.ballRadius);
      //   this.vy *= bounce;
      // } else if (this.y - this.ballRadius < top) {
      //   this.moveYTo(top + this.ballRadius);
      //   this.vy *= bounce;
      // }
    } else {
      // 重回视野
      if (this.x + x > right) {
        this.moveXTo(left - (x + width));
      } else if (this.x + x + width < left) {
        this.moveXTo(right - x);
      }
      if (this.y + y > bottom) {
        this.moveYTo(top - (y + height));
      } else if (this.y + y + height < top) {
        this.moveYTo(bottom - y);
      }
    }
  }

  // 相对坐标系原点 - 坐标系没有考虑 transform : translate rotate scale
  public getTotalLength(): number {
    return this.transformPathD().getTotalLength();
  }

  // 相对坐标系原点 - 坐标系没有考虑 transform : translate rotate scale
  // 获取插值方法1
  public getPointAtLength(length: number): {
    x: number;
    y: number;
    alpha: number;
  } {
    return this.transformPathD().getPointAtLength(length);
  }

  // 相对坐标系原点 - 坐标系没有考虑 transform : translate rotate scale
  // 获取插值方法2 t (0.0 to 1.0)
  public interpolate(t: number): { x: number; y: number } {
    return this.transformPathD().interpolate(t);
  }

  // t (0.0 to 1.0)
  public trailPath(st = 0, et = 1) {
    const totalLength = this.getTotalLength();
    const fromLength = st * totalLength;
    const toLength = et * totalLength;
    return {
      subPathD: this.transformPathD().getSubPathD(fromLength, toLength),
      point: this.getPointAtLength(toLength)
    };
  }

  // frameDuration 1帧运行时间
  public strokeDashTween(frameDuration = 1000, options?: TweenRawOptions) {
    // 未 transform 之前的 totalLength
    const totalLength = this.pathD().getTotalLength();
    this.setAttr({
      strokeDasharray: `${totalLength} ${totalLength}`,
      strokeDashoffset: `${totalLength}`
    });
    const prevOnUpdate = options?.onUpdate;
    const tl = new Timeline({ frameDuration });
    tl.addTween(
      { offset: totalLength },
      { offset: 0 },
      Object.assign(options || {}, {
        onUpdate: (coords: TweenValue, elapsed: number) => {
          this.setAttr("strokeDashoffset", "" + maxDotNumber(coords.offset, 2));
          prevOnUpdate?.(coords, elapsed);
        }
      })
    );
    tl.play();
  }

  // const rosetta = Shape.generateRosetta(120, 120, 55, 35, 6);
  public static generateRosetta(
    x: number,
    y: number,
    rx: number,
    ry: number,
    n: number
  ): Shape {
    if (n == 0) {
      throw new Error("no dividing by zero, please");
    }
    const lines: L[] = [];
    const angle = 360 / n; // negative values of n are fine
    for (let c = 0; c < n; c += 1) {
      // need angle for each leaf of rosetta in radians
      const theta = (angle * c * Math.PI) / 180;
      // coords of farthest point from center for this leaf
      const dx = x + 2 * rx * Math.cos(theta);
      const dy = y + 2 * rx * Math.sin(theta);
      lines.push(
        new Arc({
          x: x,
          y: y,
          rx,
          ry,
          xAxisRotate: angle * c,
          largeArcFlag: 1,
          sweepFlag: 1,
          toX: dx,
          toY: dy
        })
      );
      lines.push(
        new Arc({
          x: dx,
          y: dy,
          rx,
          ry,
          xAxisRotate: angle * c,
          largeArcFlag: 1,
          sweepFlag: 1,
          toX: x,
          toY: y
        })
      );
    }
    return new Shape({ initD: PathD.fromLines(lines) });
  }
}

export type ShapeComboOptions = {
  svg?: string;
  forceWidth?: number;
} & ShapeAttributeOptions &
  ShapeTransformOptions &
  ShapeDrawOptions;

export class ShapeCombo {
  public shapes: Shape[] = [];

  public svg?: string;
  public forceWidth?: number;

  public attrs: Record<string, string | CanvasGradient> = {};
  public style: Record<string, string> = {};

  public initCenterX?: number;
  public initCenterY?: number;

  public x: number;
  public y: number;

  public degree: number;

  public scaleX: number;
  public scaleY: number;

  public interactive: boolean;
  public drag?: ShapeDrawOptions["drag"];

  constructor(options?: ShapeComboOptions) {
    this.initCenterX = options?.centerX;
    this.initCenterY = options?.centerY;

    this.x = options?.x || 0;
    this.y = options?.y || 0;

    this.degree = options?.degree || 0;

    this.scaleX = 1;
    this.scaleY = 1;
    if (options?.scale !== void 0) {
      this.scaleX = options.scale;
      this.scaleY = options.scale;
    }
    if (options?.scaleX !== void 0) {
      this.scaleX = options.scaleX;
    }
    if (options?.scaleY !== void 0) {
      this.scaleY = options.scaleY;
    }

    if (options?.attrs) {
      this.setAttr(options.attrs);
    }
    if (options?.style) {
      this.setStyle(options.style);
    }

    // UNDO：陆续添加到 getBaseOptions() addShape()
    this.interactive =
      typeof options?.interactive == "boolean" ? options?.interactive : true;
    this.drag = options?.drag;

    this.svg = options?.svg;
    this.forceWidth = options?.forceWidth;

    // get ShapeCombo from parse svg
    if (options?.svg) {
      // SVG LinePicture 强制中心点为（0,0）
      this.initCenterX = 0;
      this.initCenterY = 0;
      let svgWidth = 0;
      let svgHeight = 0;
      let width = 0;
      let height = 0;
      const items = new SVGParse(options.svg).parse();
      for (let i = 0, count = items.length; i < count; i++) {
        const item = items[i];

        if (item.type == "viewbox" && item.width && item.height) {
          svgWidth = parseFloat(item.width);
          svgHeight = parseFloat(item.height);
          if (options.forceWidth) {
            const aspectRatio = svgWidth / svgHeight;
            width = options.forceWidth;
            height = width / aspectRatio;
          } else {
            width = svgWidth;
            height = svgHeight;
          }
          continue;
        }

        // 暂时只支持解析 path
        if (item.type != "path") {
          continue;
        }
        if (!isSVGPath(item.path)) {
          continue;
        }

        const attrs: Record<string, string> = {};
        for (const key in item) {
          if (key === "type" || key === "path" || key === "transform") {
            continue;
          }
          attrs[key] = item[key];
        }

        const pathDatas = absolutizeSVGPath(parseSVGPath(item.path));
        // const pathDatas = relativeSVGPath(parseSVGPath(item.path));
        const ds: SVGParsedPathData[][] = [];
        let d: SVGParsedPathData[] = [];
        for (let j = 0, allCount = pathDatas.length; j < allCount; j++) {
          const data = pathDatas[j];
          d.push(data);
          // 每遇到一个 Z 就生成一个新的 PathD
          if (data[0].toLowerCase() == "z") {
            ds.push(d);
            d = [];
          }
        }
        if (d.length) {
          ds.push(d);
        }
        ds.forEach((d) => {
          const shape = new Shape({
            attrs,
            initD: d
          });
          if (item.transform) {
            const matrix = parseTransformToMatrix(item.transform);
            const s = matrix.split();
            shape.moveTo(s.x, s.y);
            shape.rotateTo(s.degree);
            shape.scaleTo(s.scaleX, s.scaleY);
          }
          this.addShape(shape);
        });
      }

      if (width && svgWidth && height && svgHeight) {
        this.comboScale(width / svgWidth, height / svgHeight, true);
      }
    }
  }

  public destroy() {
    this.shapes.forEach((s) => s.destroy());
  }

  public getBaseOptions() {
    return {
      svg: this.svg,
      forceWidth: this.forceWidth,

      attrs: { ...this.attrs },
      style: { ...this.style },

      centerX: this.initCenterX,
      centerY: this.initCenterY,

      x: this.x,
      y: this.y,

      degree: this.degree,

      scaleX: this.scaleX,
      scaleY: this.scaleY,

      interactive: this.interactive,
      drag: { ...this.drag }
    };
  }

  public clone() {
    const shapeCombo = this.cloneShapeCombo();

    // shapeCombo.shapes 一般在 constructor 中以 addShape() 初始化了?
    shapeCombo.shapes = this.shapes.map((s) => {
      // s.clone()可以改写，用以复制「自定义属性」
      const c = s.clone();
      if (s.parent) {
        c.parent = { ...s.parent, combo: shapeCombo };
      }
      return c;
    });

    return shapeCombo;
  }

  // 子类需覆盖！
  protected cloneShapeCombo() {
    return new ShapeCombo(this.getBaseOptions());
  }

  // 供外部使用：基本属性变更
  public update(options?: Partial<ShapeComboOptions>) {
    this.shapes.forEach((s) => s.update(options));
  }

  public addShape(
    shape: Shape,
    options?: {
      dX?: number;
      dY?: number;
      dDegree?: number;
      dScaleX?: number;
      dScaleY?: number;
      notCopyFromCombo?: boolean;
    }
  ) {
    if (options?.notCopyFromCombo !== true) {
      shape.setAttr(this.attrs);
      shape.setStyle(this.style);
      shape.interactive = shape.interactive || this.interactive;
      shape.drag = { ...this.drag, ...shape.drag };
    }
    shape.parent = {
      combo: this,
      dX: options?.dX || 0,
      dY: options?.dY || 0,
      dDegree: options?.dDegree || 0,
      dScaleX: options?.dScaleX || 1,
      dScaleY: options?.dScaleY || 1
    };
    this.shapes.push(shape);
  }

  public comboMove(dx: number, dy: number, justChild = false) {
    if (!justChild) {
      this.x += dx;
      this.y += dy;
    }
    this.shapes.forEach((s) => {
      s.move(dx, dy);
    });
  }
  public comboRotate(degree: number, justChild = false) {
    if (!justChild) {
      this.degree += degree;
    }
    this.shapes.forEach((s) => {
      s.rotate(degree);
    });
  }
  public comboScale(scaleX: number, scaleY?: number, justChild = false) {
    if (!justChild) {
      this.scaleX *= scaleX;
      this.scaleY *= scaleY || scaleX;
    }
    this.shapes.forEach((s) => {
      s.scale(scaleX, scaleY);
    });
  }

  public comboTransform(
    options?: {
      x?: number;
      y?: number;
      degree?: number;
      scale?: number;
      scaleX?: number;
      scaleY?: number;
    },
    justChild = false
  ) {
    if (typeof options?.x == "number" || typeof options?.y == "number") {
      this.comboMove(options?.x || 0, options?.y || 0, justChild);
    }
    if (typeof options?.degree == "number") {
      this.comboRotate(options?.degree || 0, justChild);
    }
    if (
      typeof options?.scale == "number" ||
      typeof options?.scaleX == "number" ||
      typeof options?.scaleY == "number"
    ) {
      this.comboScale(
        options?.scaleX || options?.scale || 1,
        options?.scaleY || options?.scale || 1,
        justChild
      );
    }
  }

  // 子类可覆盖
  // 相对坐标系原点 - 坐标系没有考虑 transform : translate rotate scale
  public getBoundingClientRect(dot = 2) {
    const X: number[] = [];
    const Y: number[] = [];
    this.shapes.forEach((s) => {
      const rect = s.getBoundingClientRect(dot);
      X.push(rect.x, rect.x + rect.width);
      Y.push(rect.y, rect.y + rect.height);
    });
    if (X.length == 0 || Y.length == 0) {
      return { x: 0, y: 0, width: 0, height: 0 };
    }
    const xmin = Math.min(...X);
    const ymin = Math.min(...Y);
    const xmax = Math.max(...X);
    const ymax = Math.max(...Y);
    return {
      x: maxDotNumber(xmin, dot),
      y: maxDotNumber(ymin, dot),
      width: maxDotNumber(xmax - xmin, dot),
      height: maxDotNumber(ymax - ymin, dot)
    };
  }

  // 相对坐标系原点 - 坐标系没有考虑 transform : translate rotate scale
  public getBoundingClientRectCenter(dot = 2): { x: number; y: number } {
    if (
      typeof this.initCenterX == "number" &&
      typeof this.initCenterY == "number"
    ) {
      // 强制设置了中心点
      return { x: this.initCenterX, y: this.initCenterY };
    }
    const rect = this.getBoundingClientRect(dot);
    return {
      x: maxDotNumber(rect.x + rect.width * 0.5, dot),
      y: maxDotNumber(rect.y + rect.height * 0.5, dot)
    };
  }

  public setStyle(obj: Record<string, string>): void {
    Object.assign(this.style, obj);
    this.shapes.forEach((s) => {
      s.setStyle(obj);
    });
  }

  public setAttr(obj: Record<string, string | CanvasGradient>): void;
  public setAttr(name: string, value: string | CanvasGradient): void;
  public setAttr(
    nameOrObj: string | Record<string, string | CanvasGradient>,
    value?: string | CanvasGradient
  ) {
    if (typeof nameOrObj !== "string") {
      for (const k in nameOrObj) {
        const v = nameOrObj[k];
        if (v && v != this.getAttr(k)) {
          this.attrs[toKebab(k)] = v;
        }
      }
      this.shapes.forEach((s) => {
        s.setAttr(nameOrObj);
      });
    } else {
      if (value && value != this.getAttr(nameOrObj)) {
        this.attrs[toKebab(nameOrObj)] = value;
        this.shapes.forEach((s) => {
          s.setAttr(nameOrObj, value);
        });
      }
    }
  }

  public getAttr(
    name: string,
    defaultValue: string | CanvasGradient
  ): string | CanvasGradient;
  public getAttr(name: string): string | CanvasGradient | undefined;
  public getAttr(
    name: string,
    defaultValue?: string | CanvasGradient
  ): string | CanvasGradient | undefined {
    return this.attrs[name] || this.attrs[toKebab(name)] || defaultValue;
  }

  public hasAttr(name: string): boolean {
    return !!this.getAttr(name);
  }

  // 从小到大排序
  public sort(): void {
    this.shapes
      .map((shape) => {
        return {
          shape,
          perimeter: shape.transformPathD().getPolymorphPointsPerimeter()
        };
      })
      .sort((a, b) => b.perimeter - a.perimeter)
      .map((a) => a.shape);
  }

  public expendTo(larger: ShapeCombo): void {
    const largeLen = larger.shapes.length;
    const smallLen = this.shapes.length;
    if (largeLen < smallLen) {
      return larger.expendTo(this);
    }
    for (let i = smallLen; i < largeLen; i++) {
      const shape = larger.shapes[i];
      // this.addShape(
      //   new Shape({
      //     initD: shape.transformPathD().clone()
      //   }),
      //   { notCopyFromCombo: true }
      // );
      this.addShape(
        new Shape({
          initD: shape.pathD().clone()
        }),
        {
          dX: shape.x - this.x,
          dY: shape.y - this.y,
          dDegree: shape.degree - this.degree,
          dScaleX: shape.scaleX / this.scaleX,
          dScaleY: shape.scaleY / this.scaleY,
          notCopyFromCombo: true
        }
      );
    }
  }

  public strokeDashTween(
    frameDuration?: number,
    options?: Omit<TweenRawOptions, "onStart" | "onUpdate" | "onComplete"> & {
      type?: "parallel" | "sequence";
      onGroupStart?: () => void;
      onGroupUpdate?: () => void;
      onGroupComplete?: () => void;
    }
  ): void {
    switch (options?.type) {
      case "sequence":
        this.shapes.forEach((s, i) => {
          s.strokeDashTween(frameDuration, {
            ...options, // 注意 options 浅拷贝
            delay: i,
            onStart: () => {
              if (i == 0) {
                options?.onGroupStart?.();
              }
            },
            onUpdate: () => {
              options?.onGroupUpdate?.();
            },
            onComplete: () => {
              if (i == this.shapes.length - 1) {
                options?.onGroupComplete?.();
              }
            }
          });
        });
        break;
      case "parallel":
      default:
        // eslint-disable-next-line no-case-declarations
        const tl = new Timeline({ frameDuration });
        // eslint-disable-next-line no-case-declarations
        const from: TweenValue = {};
        // eslint-disable-next-line no-case-declarations
        const to: TweenValue = {};
        this.shapes.forEach((s, i) => {
          // 未 transform 之前的 totalLength
          const totalLength = s.pathD().getTotalLength();
          from[`offset_${i}`] = totalLength;
          to[`offset_${i}`] = 0;
          s.setAttr({
            strokeDasharray: `${totalLength} ${totalLength}`,
            strokeDashoffset: `${totalLength}`
          });
        });
        tl.addTween(
          from,
          to,
          Object.assign(options || {}, {
            onStart: () => {
              options?.onGroupStart?.();
            },
            onUpdate: (coords: TweenValue, elapsed: number) => {
              for (const c in coords) {
                const index = extractNumber(c).number;
                this.shapes[index].setAttr(
                  "strokeDashoffset",
                  "" + maxDotNumber(coords[c], 2)
                );
              }
              options?.onGroupUpdate?.();
            },
            onComplete: () => {
              options?.onGroupComplete?.();
            }
          })
        );
        tl.play();
        break;
    }
  }
}

// 跟随鼠标
export function followMouseShape(
  shape: Shape,
  mpg: MousePointGroup,
  margin = 5
) {
  const m = withinErrorMarginNumber(margin);
  if (!m(shape.x, mpg.current.x) || !m(shape.y, mpg.current.y)) {
    shape.followTo(mpg.current);
  }
}

// 鼠标放开后运动到 target
export function targetToAfterDropShape(
  shape: Shape,
  target: { x: number; y: number },
  options?: ShapeAnimateConstantOptions
) {
  if (shape.drag && shape.drag.enable && !shape.isDragging) {
    shape.animateEaseTo(target, options);
  }
}

// 运动[加速度+重力+摩擦力] +碰撞检测 (+回弹检测) (+万有引力)
export function animateShapes(
  shapes: Shape[],
  options?: ShapeAnimateConstantOptions
): number[][] {
  const alphas: number[][] = []; // [fromX, fromY, toX, toY, alpha][]
  const numParticles = shapes.length;
  for (let i = 0; i < numParticles; i++) {
    const s = shapes[i];
    if (options?.springLength) {
      // SpringConnect
      const springLength = options?.springLength || 100;
      if (!s.isDragging) {
        shapes
          .filter((_s) => _s.id !== s.id)
          .forEach((_s) => {
            const radian = angle(s.x, s.y, _s.x, _s.y);
            s.animateSpringTo(
              {
                x: _s.x - Math.cos(radian) * springLength,
                y: _s.y - Math.sin(radian) * springLength
              },
              options
            );
          });
      }
    } else if (options?.spring) {
      // Spring
      if (i > 0) {
        s.animateSpringTo(shapes[i - 1], options);
      }
    } else {
      s.animate(options);
    }
    if (options?.wall) {
      s.checkWall(options);
    }
    if (
      options?.enableCollision ||
      options?.enableGravityBetweenObjects ||
      options?.enableSpringMassAlpha
    ) {
      for (let j = i + 1; j < numParticles; j++) {
        if (options?.enableCollision) {
          s.checkCollision(shapes[j]);
        }
        if (options?.enableGravityBetweenObjects) {
          s.gravitateTo(shapes[j]);
        }
        if (options?.enableSpringMassAlpha) {
          alphas.push([
            s.x,
            s.y,
            shapes[j].x,
            shapes[j].y,
            s.getSpringMassAlphaTo(shapes[j], options)
          ]);
        }
      }
    }
  }
  return alphas;
}

function getShapesInterpolator(shapes: Shape[]): (
  offset: number // offset (0.0 to 1.0)
) => Shape {
  if (shapes.length < 2) {
    throw new Error("shapes个数必须大于等于2");
  }
  const hlen = shapes.length - 1;
  const ds = new Array(hlen);
  for (let h = 0; h < hlen; h++) {
    const pathd1 = shapes[h].transformPathD();
    const pathd2 = shapes[h + 1].transformPathD();
    const pathd1Points = pathd1.getPolymorphPoints();
    const pathd2Points = pathd2.getPolymorphPoints();
    const maxPoints =
      pathd1Points.length >= pathd2Points.length ? pathd1Points : pathd2Points;
    ds[h] = PathD.getInterpolator(
      pathd1.expendToPolymorphPoints(maxPoints),
      pathd2.expendToPolymorphPoints(maxPoints)
    );
  }
  // 该 shape 无 toSVGEl()，所以可以放心 Polymorph
  const shape = new Shape({ initD: "" });
  return (offset: number): Shape => {
    const d = maxDotNumber(hlen * offset, 6);
    const flr = Math.min(Math.floor(d), hlen - 1);
    const t = maxDotNumber(d - flr, 6);
    shape.pathD(ds[flr](t));
    shape.setAttr(shapes[flr + 1].attrs);
    // const fill1Style = shapes[flr].getAttr("fill", "none");
    // const fill2Style = shapes[flr + 1].getAttr("fill", "none");
    // if (typeof fill1Style == "string" && typeof fill2Style == "string") {
    //   shape.setAttr(
    //     "fill",
    //     Color.gradient(fill1Style, fill2Style, t).toString()
    //   );
    // }
    // const stroke1Style = shapes[flr].getAttr("stroke", "none");
    // const stroke2Style = shapes[flr + 1].getAttr("stroke", "none");
    // if (typeof stroke1Style == "string" && typeof stroke2Style == "string") {
    //   shape.setAttr(
    //     "stroke",
    //     Color.gradient(stroke1Style, stroke2Style, t).toString()
    //   );
    // }
    return shape;
  };
}

function getShapeCombosInterpolator(shapeCombos: (Shape | ShapeCombo)[]): (
  offset: number // offset (0.0 to 1.0)
) => ShapeCombo {
  if (shapeCombos.length < 2) {
    throw new Error("shapeCombos个数必须大于等于2");
  }
  let maxShapeCombo: ShapeCombo;
  const combos: ShapeCombo[] = [];
  shapeCombos.forEach((c) => {
    let combo: ShapeCombo;
    if (c instanceof ShapeCombo) {
      combo = c;
      combo.sort();
    } else {
      combo = new ShapeCombo();
      combo.addShape(c);
    }
    combos.push(combo);
    if (!maxShapeCombo || maxShapeCombo.shapes.length < combo.shapes.length) {
      maxShapeCombo = combo;
    }
  });
  const shapess: Shape[][] = [];
  combos.forEach((c, i) => {
    if (c !== maxShapeCombo) {
      c.expendTo(maxShapeCombo);
    }
    c.shapes.forEach((s, j) => {
      shapess[j] = shapess[j] || [];
      shapess[j][i] = s;
    });
  });
  const interpolators: ((offset: number) => Shape)[] = [];
  shapess.forEach((shapes) => {
    interpolators.push(getShapesInterpolator(shapes));
  });
  const combo = new ShapeCombo();
  interpolators.forEach((interpolator) => {
    combo.addShape(interpolator(0));
  });
  return (offset: number): ShapeCombo => {
    interpolators.forEach((interpolator) => {
      interpolator(offset);
    });
    return combo;
  };
}

export function polymorphTween(
  shapes: (Shape | ShapeCombo)[],
  frameDuration = 6000,
  options?: TweenRawOptions & {
    shapeOrCombo?: (s: Shape | ShapeCombo, offset: number) => void;
  }
): Shape | ShapeCombo {
  const interpolator =
    shapes.findIndex((s) => s instanceof ShapeCombo) >= 0
      ? getShapeCombosInterpolator(shapes)
      : getShapesInterpolator(shapes as Shape[]);
  const shapeOrCombo = interpolator(0);
  const prevOnUpdate = options?.onUpdate;
  const tl = new Timeline({ frameDuration });
  tl.addTween(
    { t: 0 },
    { t: 1 },
    Object.assign(options || {}, {
      onUpdate: (coords: TweenValue, elapsed: number) => {
        prevOnUpdate?.(coords, elapsed);
        interpolator(coords.t);
        options?.shapeOrCombo?.(shapeOrCombo, coords.t);
      }
    })
  );
  tl.play();
  return shapeOrCombo;
}
